import { routeAskId, routeGroupAskId } from "./types";
import type { BuildRouteWalkResult, RouteFlag, RouteWalkItem } from "./types";
import type { GapTranslate } from "../findCableGaps";
import type { KarlAnswer, KarlAsk, KarlAskTool } from "../karlAsks";

export interface BuildRouteAsksArgs {
  walk: BuildRouteWalkResult;
  /**
   * Circuit ids the estimator has said are wrong. The follow-up exists only for
   * these: asking "where do those two belong?" of a route he has just confirmed
   * is a question nobody put, and counting it up front made a walk of 83 routes
   * read as 116 questions.
   */
  corrected?: Set<string>;
  t: GapTranslate;
}

export type BuildRouteAsks = (args: BuildRouteAsksArgs) => KarlAsk[];

const line = (
  t: GapTranslate,
  key: string,
  params?: Record<string, string | number>,
): string => t(`smartSurveyor.cablesBeta.${key}`, params);

const later = (t: GapTranslate): KarlAnswer => ({
  id: "later",
  label: line(t, "askLater"),
  tone: "quiet",
  action: { kind: "skip" },
});

const whyDetail = (item: RouteWalkItem, t: GapTranslate): string | undefined => {
  const { why } = item;
  const key = why.key ?? item.label;
  const board = why.board ?? item.board;

  if (why.kind === "key_on_plan") {
    return line(t, "askRouteWhyPlan", { key, cableType: why.cableType });
  }
  if (why.kind === "key_from_schematic") {
    return line(t, "askRouteWhySchematic", {
      key,
      board,
      breaker: why.breaker ?? key,
      cableType: why.cableType,
    });
  }
  if (why.kind === "grouped_by_symbol") {
    // Two different admissions, and the difference is the one thing he can act
    // on. No schedule for this board means the document is missing and he
    // should go and ask for it; a schedule that names nothing here means the
    // rows exist and none of them fits. Guessing at a schematic reference for
    // either would be inventing evidence — on project 153 all 107 schedule rows
    // are under NSHV and every symbol-grouped run hangs off GHV, DV, BMZ or
    // PAS/HES, so there is genuinely nothing to point at.
    if (why.scheduleRowsForBoard === 0) {
      return line(t, "askRouteWhySymbolNoSchedule", {
        board,
        cableType: why.cableType,
      });
    }
    return line(t, "askRouteWhySymbolUnmatched", {
      board,
      rows: why.scheduleRowsForBoard,
      cableType: why.cableType,
    });
  }
  // A board-only reason with no board left to name says nothing worth a line.
  if (!board) return undefined;
  return line(t, "askRouteWhyBoard", { board, cableType: why.cableType });
};

// Not answers. Yes/no settles whether the grouping is right; these change what
// the run carries, and the question stays open until he says.
const routeTools = (circuitId: string): KarlAskTool[] => [
  {
    id: "addDevices",
    labelKey: "toolAddDevices",
    action: { kind: "boxAssign", circuitId },
  },
  {
    id: "removeDevices",
    labelKey: "toolRemoveDevices",
    action: { kind: "boxRemove", circuitId },
  },
  {
    // Opens the shape-edit mode on this run: its nodes get handles and become
    // grabbable. It used to be `accept`, which armed nothing and recorded
    // nothing — a button that only restated that dragging existed.
    id: "redrawRoute",
    labelKey: "toolRedrawRoute",
    action: { kind: "editRouteShape", circuitId },
  },
];

// Every name the way answers to, strongest first. One schematic page carries the
// breaker, the cable id and the handwritten plan tag alike, and which of them its
// occurrences record varies by project — so the card is handed all three and takes
// the first that lands. The key is here too: a breaker column that was empty falls
// back to the key in `buildWays`, so the two can be the same string.
const diagramRefsOf = (item: RouteWalkItem): string[] => {
  const refs: string[] = [];
  for (const name of [item.why.breaker, item.why.cableId, item.why.key]) {
    const trimmed = name?.trim() ?? "";
    if (!trimmed || refs.includes(trimmed)) continue;
    refs.push(trimmed);
  }
  return refs;
};

const routeAsk = (item: RouteWalkItem, t: GapTranslate): KarlAsk => {
  const detail = whyDetail(item, t);
  const diagramRefs = diagramRefsOf(item);

  return {
    id: routeAskId(item.circuitId),
    group: "routes",
    question: line(t, "askRoute", {
      key: item.label,
      board: item.board,
      count: item.deviceCount,
    }),
    ...(detail ? { detail } : {}),
    tools: routeTools(item.circuitId),
    subject: item.label,
    subjectKey: item.circuitId,
    focus: { positions: item.positions, circuitId: item.circuitId },
    ...(diagramRefs.length ? { diagramRefs } : {}),
    answers: [
      {
        id: "right",
        label: line(t, "askRouteRight"),
        tone: "primary",
        action: { kind: "accept" },
      },
      {
        id: "some",
        label: line(t, "askRouteSome"),
        // Was `drawBox`, which armed a box that caught devices, changed no
        // data, marked the run corrected and moved on. Boxing them OUT is what
        // "some don't belong" means, and it raises the question of where they
        // go instead.
        action: { kind: "boxRemove", circuitId: item.circuitId },
      },
      later(t),
    ],
  };
};

const openFlags = (item: RouteWalkItem): RouteFlag[] =>
  item.flags.filter(
    (flag) => flag.kind === "stray_devices" || flag.kind === "unkeyed_bag",
  );

const routeGroupAsk = (
  item: RouteWalkItem,
  flags: RouteFlag[],
  t: GapTranslate,
): KarlAsk => {
  const count = flags.reduce((total, flag) => total + flag.devices, 0);
  const positions = flags.flatMap((flag) => flag.positions);
  const destinationIds = flags.flatMap((flag) => flag.destinationIds);
  // Deterministic, so an answer can park the devices AND arm the pick in one
  // move: a group id minted inside the edit layer is not known until after.
  const groupId = `rmflag-${item.circuitId}`;

  const answers: KarlAnswer[] = destinationIds.length
    ? [
        {
          id: "toRoute",
          label: line(t, "askRouteToRoute"),
          tone: "primary",
          action: { kind: "parkAndPick", groupId, destinationIds },
        },
        {
          id: "out",
          label: line(t, "askRouteOut"),
          action: { kind: "parkDevices", groupId, destinationIds },
        },
        later(t),
      ]
    : // A bag has no per-device identity, so nothing here can move one. Saying
      // so beats offering two buttons that cannot do what they read as.
      [
        {
          id: "noted",
          label: line(t, "askRouteGroupNoted"),
          tone: "primary",
          action: { kind: "accept" },
        },
        later(t),
      ];

  return {
    id: routeGroupAskId(item.circuitId),
    group: "routes",
    question: line(t, "askRouteGroup", { count }),
    subject: item.label,
    subjectKey: item.circuitId,
    // `circuitId` as well as the positions: without it `routeUnderReview` went
    // null and all 83 runs flooded back onto the plan mid-question — the exact
    // "too messy" this stage exists to cure.
    focus: { circuitId: item.circuitId, ...(positions.length ? { positions } : {}) },
    answers,
  };
};

export const buildRouteAsks: BuildRouteAsks = ({ walk, corrected, t }) =>
  walk.routes.flatMap((item) => {
    const flags = openFlags(item);
    if (!flags.length || !corrected?.has(item.circuitId)) {
      return [routeAsk(item, t)];
    }
    return [routeAsk(item, t), routeGroupAsk(item, flags, t)];
  });
